<template>
  <section class="card">
    <header class="head">
      <h2>会费定价（按会员级别）</h2>
      <button type="button" class="btn btn-primary" @click="openCreateDialog">新增定价</button>
    </header>
    <p class="note">
      维护「会员级别 → 会费」。支持：固定年费（FLAT）、首年+续费（FIRST_RENEWAL，如副会长单位首年 2 万续费 1 万）、按届总额（TERM，如非本行业四年一届，金额填一届总额，届长填 4）。
      入会出账与会员列表「参考金额」均走同一套计算逻辑。改价后仅影响<strong>新写入或待补金额</strong>的订单；已生成订单请秘书在库里或后续「重算订单」功能中处理。
    </p>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="keyword"
          class="flt-input flt-input--wide"
          placeholder="关键字（级别/备注）"
          aria-label="关键字，级别或备注"
          @keyup.enter="applyFilters"
        />
      </div>
      <div class="flt">
        <FancySelect
          v-model="memberLevelFilter"
          class="flt-select flt-select--wide"
          :options="toolbarMemberLevelFilterOptions"
          placeholder="会员级别"
          searchable
        />
      </div>
      <div class="flt">
        <FancySelect
          v-model="pricingModeFilter"
          class="flt-select"
          :options="toolbarPricingModeFilterOptions"
          placeholder="计价方式"
        />
      </div>
      <div class="flt">
        <FancySelect v-model="statusFilter" class="flt-select" :options="toolbarStatusOptions" placeholder="状态" />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" @click="applyFilters">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <table v-if="rows.length">
      <thead>
        <tr>
          <th class="col-seq">序号</th>
          <th>会员级别</th>
          <th class="col-mode">计价</th>
          <th class="col-amt">金额说明</th>
          <th class="col-status">状态</th>
          <th>备注</th>
          <th class="col-center col-ops">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(r, idx) in displayRows" :key="r.id">
          <td class="col-seq">{{ idx + 1 }}</td>
          <td>{{ r.memberLevel }}</td>
          <td class="col-mode">{{ pricingModeLabel(r.pricingMode) }}</td>
          <td class="col-amt">{{ amountSummary(r) }}</td>
          <td class="col-status">
            <span class="pill" :class="r.statusCode === 'ENABLED' ? 'pill-green' : 'pill-red'">
              {{ r.statusCode === 'ENABLED' ? '启用' : '停用' }}
            </span>
          </td>
          <td class="ellipsis" :title="r.remark || ''">{{ r.remark || '—' }}</td>
          <td class="col-center col-ops actions">
            <button type="button" class="btn btn-edit" @click="editRow(r)">编辑</button>
            <button type="button" class="btn btn-delete" @click="removeRow(r)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="empty-state">
      <p class="empty-title">暂无定价</p>
      <p class="empty-desc">先维护会员级别对应的会费金额，系统才会自动生成正确金额的会费订单。</p>
      <button type="button" class="btn btn-primary" @click="openCreateDialog">立即新增定价</button>
    </div>

    <!-- 弹窗 -->
    <div v-if="showDialog" class="mask" @click.self="closeDialog">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ form.id ? '编辑定价' : '新增定价' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
<p class="dialog-hint muted">会员级别与会员管理编辑弹窗同一套选项；编辑旧数据时若不在列表中会保留「当前值」。</p>
        <div class="form">
          <label class="field field-full">
            <span>会员级别</span>
            <FancySelect
              v-model="form.memberLevel"
              class="field-select"
              :options="pricingMemberLevelDutyOptions"
              placeholder="选择会员级别"
              searchable
            />
          </label>
          <label class="field field-full">
            <span>计价方式</span>
            <FancySelect v-model="form.pricingMode" :options="pricingModeOptions" placeholder="选择计价方式" />
            <small class="hint">FLAT=每年同一价；FIRST_RENEWAL=首年与续费不同；TERM=按届收一次（金额为一届总额）。</small>
          </label>
          <label v-if="form.pricingMode === 'FIRST_RENEWAL'" class="field">
            <span>首年金额（元）</span>
            <input v-model.trim="form.firstYearYuan" placeholder="如 20000，可空则用下方年费" />
          </label>
          <label v-if="form.pricingMode === 'FIRST_RENEWAL'" class="field">
            <span>续费每年（元）</span>
            <input v-model.trim="form.renewalYuan" placeholder="如 10000，可空则用下方年费" />
          </label>
          <label v-if="form.pricingMode === 'TERM'" class="field">
            <span>一届年数</span>
            <input v-model.trim="form.termYears" placeholder="如 4" />
          </label>
          <label class="field" :class="{ 'field-full': form.pricingMode === 'TERM' }">
            <span>{{ form.pricingMode === 'TERM' ? '一届总额（元）' : '年费 / 回退金额（元）' }}</span>
            <input v-model.trim="form.amountYuan" placeholder="例如：3000 或 20000" />
            <small class="hint">保存时换算为分。FIRST_RENEWAL 下本字段可作首年/续费的回退价；TERM 下为一届总金额。</small>
          </label>
          <label class="field">
            <span>状态</span>
            <FancySelect v-model="form.statusCode" :options="formStatusOptions" placeholder="选择状态" />
          </label>
          <label class="field field-full">
            <span>备注（可空）</span>
            <textarea v-model.trim="form.remark" placeholder="例如：2026 年度会费标准" />
          </label>
        </div>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="closeDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="saving" @click="saveRow">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import {
  getMembershipFeePrices,
  createMembershipFeePrice,
  updateMembershipFeePrice,
  deleteMembershipFeePrice
} from '@/api'
import {
  ADMIN_MEMBER_LEVEL_OPTIONS,
  toMemberLevelSelectOptions,
} from '@/utils/adminMemberLevelOptions'

export default {
  name: 'FeePricingManageView',
  components: { FancySelect },
  data() {
    return {
      loading: false,
      saving: false,
      errorMsg: '',
      keyword: '',
      memberLevelFilter: '',
      pricingModeFilter: '',
      statusFilter: '',
      rows: [],
      showDialog: false,
      form: {
        id: null,
        memberLevel: '',
        pricingMode: 'FLAT',
        firstYearYuan: '',
        renewalYuan: '',
        termYears: '',
        amountYuan: '',
        statusCode: 'ENABLED',
        remark: ''
      },
    }
  },
  computed: {
    toolbarStatusOptions() {
      return [
        { label: '全部状态', value: '' },
        { label: '启用', value: 'ENABLED' },
        { label: '停用', value: 'DISABLED' },
      ]
    },
    toolbarPricingModeFilterOptions() {
      return [
        { label: '全部计价', value: '' },
        { label: '固定年费', value: 'FLAT' },
        { label: '首年+续费', value: 'FIRST_RENEWAL' },
        { label: '按届', value: 'TERM' },
      ]
    },
    /** 后台完整会员级别 + 当前表格中的级别，便于按级别筛选 */
    toolbarMemberLevelFilterOptions() {
      const out = [{ label: '全部级别', value: '' }]
      const seen = new Set([''])
      const add = (value, label) => {
        const v = String(value || '').trim()
        if (!v || seen.has(v)) return
        seen.add(v)
        out.push({ value: v, label: String(label || '').trim() || v })
      }
      for (const o of toMemberLevelSelectOptions(ADMIN_MEMBER_LEVEL_OPTIONS)) add(o.value, o.label)
      const fromRows = [
        ...new Set((this.rows || []).map((r) => String(r.memberLevel || '').trim()).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b, 'zh-CN'))
      for (const lv of fromRows) add(lv, lv)
      return out
    },
    formStatusOptions() {
      return [
        { label: '启用', value: 'ENABLED' },
        { label: '停用', value: 'DISABLED' },
      ]
    },
    pricingModeOptions() {
      return [
        { label: '固定年费 FLAT', value: 'FLAT' },
        { label: '首年+续费 FIRST_RENEWAL', value: 'FIRST_RENEWAL' },
        { label: '按届 TERM', value: 'TERM' },
      ]
    },
    displayRows() {
      const kw = String(this.keyword || '').trim().toLowerCase()
      const st = String(this.statusFilter || '').trim().toUpperCase()
      const ml = String(this.memberLevelFilter || '').trim()
      const pm = String(this.pricingModeFilter || '').trim().toUpperCase()
      return (this.rows || []).filter((r) => {
        if (pm && String(r.pricingMode || 'FLAT').toUpperCase() !== pm) return false
        if (ml && String(r.memberLevel || '').trim() !== ml) return false
        if (st && String(r.statusCode || '').toUpperCase() !== st) return false
        if (!kw) return true
        const t = `${r.memberLevel || ''} ${r.remark || ''}`.toLowerCase()
        return t.includes(kw)
      })
    },
    /** 与会员管理编辑弹窗同一套级别；当前值不在列表时兜底 */
    pricingMemberLevelDutyOptions() {
      const base = toMemberLevelSelectOptions(ADMIN_MEMBER_LEVEL_OPTIONS)
      const ml = String(this.form?.memberLevel || '').trim()
      const seen = new Set(base.map((o) => String(o.value)))
      const tail = []
      if (ml && !seen.has(ml)) {
        tail.push({ value: ml, label: `${ml}（当前值）` })
      }
      return [...base, ...tail]
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    yuan(amountCent) {
      const n = Number(amountCent || 0)
      if (!Number.isFinite(n) || n <= 0) return '0.00'
      return (n / 100).toFixed(2)
    },
    pricingModeLabel(mode) {
      const m = String(mode || 'FLAT').toUpperCase()
      if (m === 'FIRST_RENEWAL') return '首年+续费'
      if (m === 'TERM') return '按届'
      return '固定年费'
    },
    amountSummary(r) {
      const m = String(r?.pricingMode || 'FLAT').toUpperCase()
      if (m === 'FIRST_RENEWAL') {
        const f = r.firstYearAmountCent != null && r.firstYearAmountCent > 0 ? this.yuan(r.firstYearAmountCent) : this.yuan(r.amountCent)
        const ren = r.renewalAmountCent != null && r.renewalAmountCent > 0 ? this.yuan(r.renewalAmountCent) : this.yuan(r.amountCent)
        return `首年 ¥${f} / 续费 ¥${ren}`
      }
      if (m === 'TERM') {
        const y = r.termYears != null ? r.termYears : '—'
        return `一届${y}年 ¥${this.yuan(r.amountCent)}`
      }
      return `¥ ${this.yuan(r.amountCent)}`
    },
    amountCentFromYuan(raw) {
      const s = String(raw || '').trim()
      if (!s) return 0
      if (!/^\d+(\.\d{1,2})?$/.test(s)) throw new Error('金额格式不正确（最多两位小数）')
      const n = Number(s)
      if (!Number.isFinite(n) || n < 0) throw new Error('金额必须是非负数字')
      return Math.round(n * 100)
    },
    async load() {
      this.errorMsg = ''
      this.loading = true
      try {
        const list = await getMembershipFeePrices('')
        this.rows = Array.isArray(list) ? list : []
      } catch (e) {
        this.errorMsg = e?.message || '加载失败'
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    applyFilters() {
      // computed 过滤即可
    },
    resetFilters() {
      this.keyword = ''
      this.memberLevelFilter = ''
      this.pricingModeFilter = ''
      this.statusFilter = ''
    },
    openCreateDialog() {
      this.form = {
        id: null,
        memberLevel: '',
        pricingMode: 'FLAT',
        firstYearYuan: '',
        renewalYuan: '',
        termYears: '',
        amountYuan: '',
        statusCode: 'ENABLED',
        remark: ''
      }
      this.showDialog = true
    },
    editRow(r) {
      const mode = String(r.pricingMode || 'FLAT').toUpperCase()
      this.form = {
        id: r.id,
        memberLevel: r.memberLevel || '',
        pricingMode: ['FLAT', 'FIRST_RENEWAL', 'TERM'].includes(mode) ? mode : 'FLAT',
        firstYearYuan: (r.firstYearAmountCent != null && Number(r.firstYearAmountCent) > 0) ? this.yuan(r.firstYearAmountCent) : '',
        renewalYuan: (r.renewalAmountCent != null && Number(r.renewalAmountCent) > 0) ? this.yuan(r.renewalAmountCent) : '',
        termYears: r.termYears != null ? String(r.termYears) : '',
        amountYuan: this.yuan(r.amountCent || 0),
        statusCode: r.statusCode || 'ENABLED',
        remark: r.remark || ''
      }
      this.showDialog = true
    },
    closeDialog() {
      if (this.saving) return
      this.showDialog = false
    },
    async saveRow() {
      const level = String(this.form.memberLevel || '').trim()
      if (!level) {
        this.errorMsg = '请填写会员级别'
        return
      }
      let amountCent = 0
      try {
        amountCent = this.amountCentFromYuan(this.form.amountYuan)
      } catch (e) {
        this.errorMsg = e?.message || '金额不合法'
        return
      }
      const mode = String(this.form.pricingMode || 'FLAT').toUpperCase()
      let firstYearAmountCent = null
      let renewalAmountCent = null
      let termYears = null
      try {
        if (mode === 'FIRST_RENEWAL') {
          if (String(this.form.firstYearYuan || '').trim()) {
            firstYearAmountCent = this.amountCentFromYuan(this.form.firstYearYuan)
          }
          if (String(this.form.renewalYuan || '').trim()) {
            renewalAmountCent = this.amountCentFromYuan(this.form.renewalYuan)
          }
        }
        if (mode === 'TERM') {
          const ty = parseInt(String(this.form.termYears || '').trim(), 10)
          if (!Number.isFinite(ty) || ty < 1) throw new Error('请填写一届年数（正整数）')
          termYears = ty
        }
      } catch (e) {
        this.errorMsg = e?.message || '字段不合法'
        return
      }
      const payload = {
        memberLevel: level,
        amountCent,
        statusCode: String(this.form.statusCode || 'ENABLED').toUpperCase(),
        remark: String(this.form.remark || '').trim(),
        pricingMode: mode,
        firstYearAmountCent,
        renewalAmountCent,
        termYears
      }
      this.saving = true
      this.errorMsg = ''
      try {
        if (this.form.id) {
          await updateMembershipFeePrice(this.form.id, payload)
        } else {
          await createMembershipFeePrice(payload)
        }
        this.showDialog = false
        await this.load()
      } catch (e) {
        this.errorMsg = e?.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    async removeRow(r) {
      if (!r || r.id == null) return
      if (!await adminConfirm(`确定删除「${r.memberLevel}」的定价吗？`)) return
      this.errorMsg = ''
      try {
        await deleteMembershipFeePrice(r.id)
        await this.load()
      } catch (e) {
        this.errorMsg = e?.message || '删除失败'
      }
    }
  }
}
</script>

<style scoped>
/* 与轮播管理（BannerManageView）及确认函/缴费函模板页同一套骨架 */
.card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.head h2 {
  margin: 0;
  font-size: 18px;
  color: #101828;
}
.note {
  margin: 0 0 12px;
  background: #f5f8ff;
  border: 1px solid #dae5ff;
  color: #2a4b8d;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}
.filters .flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
}
.filters .flt-input--wide {
  width: 280px;
}
.filters .flt-select {
  width: 170px;
}
.filters .flt-select--wide {
  width: 200px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
:deep(.filters .flt-select--wide .fs-btn) {
  min-width: 200px;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  border-bottom: 1px solid #edf0f5;
  padding: 8px;
  font-size: 13px;
  text-align: left;
}
.col-seq,
.col-mode,
.col-amt,
.col-status {
  text-align: center;
  vertical-align: middle;
}
.col-amt {
  font-size: 12px;
}
.ellipsis {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.btn-delete {
  border-color: #ffc8c2;
  background: #fff1f0;
  color: #d92d20;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.actions .btn + .btn {
  margin-left: 0;
}
.error {
  color: #d92d20;
  margin: 0 0 8px;
}
.pill {
  display: inline-block;
  min-width: 58px;
  text-align: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-red {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.empty-state {
  border: 1px dashed #cfd8ee;
  border-radius: 12px;
  background: #fbfcff;
  padding: 36px 20px;
  text-align: center;
}
.empty-title {
  margin: 0;
  font-size: 18px;
  color: #1d2939;
}
.empty-desc {
  margin: 8px 0 16px;
  color: #667085;
}
.dialog-hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: #667085;
  line-height: 1.45;
}
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #344054;
}
.field > span {
  font-weight: 600;
}
.field-full {
  grid-column: 1 / -1;
}
.hint {
  color: #667085;
  font-size: 12px;
}
.form input,
.form textarea {
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  box-sizing: border-box;
  font-size: 13px;
}
.form textarea {
  min-height: 90px;
  resize: vertical;
  width: 100%;
}
.field-select {
  width: 100%;
  max-width: 420px;
}
:deep(.field-select .fs-btn) {
  height: 40px;
  border-radius: 8px;
  width: 100%;
  max-width: 420px;
}
:deep(.form .fs-btn) {
  height: 40px;
  border-radius: 8px;
}
</style>

